/**
 * BOGOS (free gift app) mutates the cart via its own AJAX calls, outside the
 * theme's add/update flows, so `cart:update` never fires and the cart icon
 * and drawer go stale. Bridges BOGOS's own cart-change events into the
 * theme's `cart:update` (SHOPUS-225) so they refresh through the paths they
 * already listen on.
 */

const BOGOS_CART_EVENTS = [
  "fg-gifts:updated",
  "fg-cart:auto-updated",
  "fg-gifts:added",
];

// BOGOS dispatches a specific event (e.g. `fg-cart:auto-updated`) and the
// general `fg-gifts:updated` for the same underlying change, ~200ms apart —
// debounce so one BOGOS change produces one theme `cart:update`.
const DEBOUNCE_MS = 400;

let debounceTimer = null;

// Monotonic guard: two BOGOS changes spaced further apart than the debounce
// each start their own `/cart.js` fetch, and nothing otherwise stops the
// older response from landing second and re-rendering the drawer with a
// stale cart (same shape as SHOP-1407 in promo-cart.js). Each run claims a
// number before fetching and discards its result if a newer run started.
let refreshSeq = 0;

// Skip re-dispatching when the cart didn't actually change — `fg-gifts:updated`
// also fires on a gift re-evaluation that changed nothing, which would
// otherwise force an uncached section re-render + drawer morph for no delta.
let lastCartSignature = null;

function cartSignature(cart) {
  return JSON.stringify({
    itemCount: cart.item_count,
    lines: cart.items.map((item) => `${item.key}:${item.quantity}`),
  });
}

function syncThemeCartAfterBogosChange() {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    const seq = ++refreshSeq;

    fetch("/cart.js")
      .then((response) => {
        if (!response.ok)
          throw new Error(`/cart.js responded ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        if (seq !== refreshSeq) return;

        const signature = cartSignature(cart);
        if (signature === lastCartSignature) return;
        lastCartSignature = signature;

        document.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: "bogos",
              data: { source: "bogos", itemCount: cart.item_count },
            },
          }),
        );
      })
      .catch((error) => {
        console.error("[bogos-cart-sync] failed to refresh cart", error);
      });
  }, DEBOUNCE_MS);
}

// Confirmed via live event trace: BOGOS dispatches these on `document` with
// `bubbles: false`. A non-capturing `window` listener only ever runs in the
// bubble phase, so with `bubbles: false` it never fires no matter what —
// `document` is the only target that actually sees these.
for (const eventName of BOGOS_CART_EVENTS) {
  document.addEventListener(eventName, syncThemeCartAfterBogosChange);
}
